import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Sheet } from "@/pages/Familia";
import { mesLabel, TIPOS_DOC } from "@/lib/todosDocs";
import { PersonaChip } from "@/components/personas/Avatar";

const GRUPOS = [
  ["persona", "Familia", ["familia", "contratos", "ocio"], "Users"], ["vivienda", "Viviendas", ["viviendas"], "Home"],
  ["vehiculo", "Vehículos", ["vehiculos"], "Car"], ["mascota", "Mascotas", ["mascotas"], "PawPrint"],
];
const TIPOS = [["", "Todos"], ...TIPOS_DOC.map(([id, label]) => [id, label]), ["otros", "Otros"]];

const Boton = ({ sel, onClick, children, testid }) => (
  <button type="button" data-testid={testid} aria-pressed={sel} onClick={onClick}
    className={`min-h-[52px] px-4 rounded-xl border-2 text-base font-semibold text-left transition-colors ${sel ? "bg-[#3A6B56] border-[#3A6B56] text-white" : "bg-white border-[#E5DFD5] text-[#1C2421] hover:border-[#3A6B56]"}`}>
    {children}
  </button>
);
const Titulo = ({ n, children }) => <h3 className="text-lg font-bold text-[#1C2421]">{n ? `${n}. ` : ""}{children}</h3>;

const opcionesQuien = (areas) => GRUPOS.map(([id, label, aids, icon]) => {
  const m = new Map();
  areas.filter((a) => aids.includes(a.id)).forEach((a) => a.entidades.forEach((e) => m.set(e.id, e.label)));
  return { id, label, icon, ents: [...m].map(([eid, l]) => ({ value: `${id}:${eid}`, label: l, pid: id === "persona" && eid !== "sin-asignar" ? eid : null })).sort((a, b) => a.label.localeCompare(b.label, "es")) };
}).filter((g) => g.ents.length);

const opcionesAsunto = (lista, quien) => {
  const m = new Map();
  lista.filter((x) => x.quien.includes(quien)).forEach((x) => x.asuntos.filter((a) => a.key.startsWith(`${quien}|`)).forEach((a) => m.set(a.key, a)));
  const porArea = {};
  [...m.values()].forEach((a) => (porArea[a.area] = porArea[a.area] || []).push(a));
  return Object.entries(porArea);
};

export const FiltrosSheet = ({ f, setF, areas, lista, total, onClose }) => {
  const [mas, setMas] = useState(!!f.mes);
  const set = (k) => (val) => setF((p) => ({ ...p, [k]: val, ...(k === "quien" ? { asunto: "" } : {}) }));
  const grupos = opcionesQuien(areas);
  const asuntos = f.quien ? opcionesAsunto(lista, f.quien) : [];
  const meses = [...new Set(lista.map((x) => x.mes).filter(Boolean))].sort().reverse();
  return (
    <Sheet onClose={onClose} testid="documentos-filtros">
      <h2 className="font-heading text-xl font-bold text-[#1C2421] pr-12">Filtrar documentos</h2>
      <div className="space-y-6 mt-5">
        <section className="space-y-3" data-testid="filtro-quien">
          <Titulo n={1}>¿De quién o de qué es?</Titulo>
          {grupos.map((g) => {
            const I = Icons[g.icon];
            return (
              <div key={g.id} className="space-y-2" data-testid={`filtro-grupo-${g.id}`}>
                <p className="text-sm font-semibold text-[#4A5550] flex items-center gap-1.5"><I size={16} /> {g.label}</p>
                <div className="grid grid-cols-2 gap-2">
                  {g.ents.map((e) => <Boton key={e.value} sel={f.quien === e.value} testid={`filtro-quien-${e.value}`} onClick={() => set("quien")(f.quien === e.value ? "" : e.value)}>{e.pid ? <PersonaChip id={e.pid} size="sm" testid={`filtro-avatar-${e.pid}`} /> : e.label}</Boton>)}
                </div>
              </div>
            );
          })}
        </section>
        {f.quien && (
          <section className="space-y-3" data-testid="filtro-asunto">
            <Titulo n={2}>¿Qué asunto quieres consultar?</Titulo>
            <Boton sel={!f.asunto} testid="filtro-asunto-todos" onClick={() => set("asunto")("")}>Todos sus asuntos</Boton>
            {asuntos.map(([area, as]) => (
              <div key={area} className="space-y-2">
                <p className="text-sm font-semibold text-[#4A5550]">{area}</p>
                <div className="flex flex-col gap-2">
                  {as.map((a) => <Boton key={a.key} sel={f.asunto === a.key} testid={`filtro-asunto-${a.key}`} onClick={() => set("asunto")(a.key)}>{a.label}</Boton>)}
                </div>
              </div>
            ))}
          </section>
        )}
        <section className="space-y-3" data-testid="filtro-tipo">
          <Titulo>Tipo de documento <span className="text-sm font-normal text-[#6E7A74]">(opcional)</span></Titulo>
          <div className="grid grid-cols-2 gap-2">
            {TIPOS.map(([id, label]) => <Boton key={id || "todos"} sel={f.tipo === id} testid={`filtro-tipo-${id || "todos"}`} onClick={() => set("tipo")(id)}>{label}</Boton>)}
          </div>
        </section>
        <section className="space-y-3">
          <button type="button" data-testid="filtro-mas" aria-expanded={mas} onClick={() => setMas(!mas)} className="min-h-[44px] inline-flex items-center gap-1 text-base font-semibold text-[#3A6B56]">
            Más filtros <Icons.ChevronDown size={18} className={mas ? "rotate-180" : ""} />
          </button>
          {mas && (
            <label className="block">
              <span className="text-sm font-semibold text-[#4A5550]">Fecha</span>
              <select data-testid="filtro-fecha" value={f.mes} onChange={(e) => set("mes")(e.target.value)}
                className="mt-1 w-full min-h-[52px] px-3 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421]">
                <option value="">Cualquier fecha</option>
                {meses.map((m) => <option key={m} value={m}>{mesLabel(m)}</option>)}
              </select>
            </label>
          )}
        </section>
      </div>
      <div className="sticky -bottom-8 bg-[#FAF8F5] pt-4 pb-2 mt-4 flex gap-2.5">
        <button data-testid="filtros-limpiar" onClick={() => setF({ quien: "", asunto: "", tipo: "", mes: "" })}
          className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Quitar filtros</button>
        <button data-testid="filtros-ver" onClick={onClose} className="flex-1 min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base">
          Ver {total} {total === 1 ? "documento" : "documentos"}
        </button>
      </div>
    </Sheet>
  );
};
